import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { Crumb } from '../lib/breadcrumbs'
import { useCrumbTrail } from '../hooks/useCrumbTrail'

// Mirrors the prototype's single `#portal-breadcrumb` bar (`renderBreadcrumb`
// and `.auto-crumb` in subscription-portal.html), rendered once by AppShell
// above every page except Home. The trail is derived from the matched route
// alone (lib/breadcrumbs.ts, via the route handles) — the product tab is the
// `/{page}/:tab` param, so no page has to report it.

function HomeCrumbIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  )
}

function ChevronSep() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

// A crumb with a target is a link (prototype `.auto-crumb a`, gap 8px);
// the one without is the current crumb (`.crumb-current`, gap 6px, weight
// 600, primary text). crumbTrail only ever leaves the last crumb unlinked —
// including Home when it is the only crumb (an unknown page).
function CrumbPiece({ to, inner }: Readonly<{ to?: string; inner: ReactNode }>) {
  if (to === undefined) {
    return (
      <span className="inline-flex items-center gap-1.5 font-semibold text-[var(--text-primary)]">
        {inner}
      </span>
    )
  }
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-2 text-[var(--text-tertiary)] hover:text-[var(--accent-text)]"
    >
      {inner}
    </Link>
  )
}

export function Breadcrumbs() {
  const { t } = useTranslation()
  const crumbs = useCrumbTrail()
  if (!crumbs) return null

  function label(crumb: Crumb): string {
    if (crumb.kind === 'home') return t('appShell.nav.home')
    // The product name is a literal, never translated (ADR §8).
    if (crumb.kind === 'tab') return crumb.product
    return t(crumb.labelKey)
  }

  return (
    // Its own fixed bar above AppShell's scroll container (prototype
    // `.auto-crumb { flex-shrink:0; padding:20px 32px 0 }`, outside the
    // scrolling area), so it stays put while the page scrolls.
    <div data-testid="breadcrumbs" className="shrink-0 flex items-center flex-wrap gap-2 px-8 pt-5 text-[13px] text-[var(--text-tertiary)]">
      {crumbs.map((crumb, i) => (
        <Fragment key={crumb.kind}>
          {i > 0 && <ChevronSep />}
          <CrumbPiece
            to={crumb.kind === 'tab' || crumb.kind === 'fixedTab' ? undefined : crumb.to}
            inner={
              <>
                {crumb.kind === 'home' && <HomeCrumbIcon />}
                {label(crumb)}
              </>
            }
          />
        </Fragment>
      ))}
    </div>
  )
}
